import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  DocumentTextIcon, 
  CloudArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

interface DocumentType {
  id: number;
  name: string;
  description?: string;
  isRequired: boolean;
  maxFileSizeMb: number;
  allowedFileTypes: string[];
}

interface DocumentRequest {
  id: number;
  organizationId: number;
  leadId: number;
  requestToken: string;
  status: 'requested' | 'submitted' | 'reviewed' | 'rejected';
  requestedAt: string;
  isReRequest?: boolean;
  lead: {
    firstName: string;
    lastName: string;
    email: string;
  };
  organization: {
    name: string;
  };
  items: Array<{
    id: number;
    documentTypeId: number;
    status: 'pending' | 'submitted' | 'accepted' | 'rejected';
    documentType: DocumentType;
    notes?: Array<{
      note: string;
      createdAt: string;
    }>;
  }>;
}

interface FileUpload {
  file: File;
  preview: string;
  error?: string;
}

const DocumentSubmissionForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [request, setRequest] = useState<DocumentRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploads, setUploads] = useState<{ [key: number]: FileUpload[] }>({});
  const [notes, setNotes] = useState<{ [key: number]: string }>({});
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [missingRequiredTypes, setMissingRequiredTypes] = useState<string[]>([]);

  useEffect(() => {
    if (token) {
      fetchRequest();
    }
  }, [token]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await import('../services/api').then(module => 
        module.publicApi.get(`/documents/public/request/${token}`)
      );
      setRequest(response.data.data);
    } catch (err: any) {
      console.error('Error fetching document request:', err);
      setError('Document request not found or expired');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (itemId: number, files: FileList | null) => {
    if (!files) return;

    const newUploads: FileUpload[] = [];
    const item = request?.items.find(i => i.id === itemId);
    if (!item) return;
    
    // Block upload for already submitted items (partial submission support)
    if (item.status === 'submitted') {
      alert('This document has already been submitted. You can only upload pending documents.');
      return;
    }

    Array.from(files).forEach(file => {
      // Validate file size
      if (file.size > item.documentType.maxFileSizeMb * 1024 * 1024) {
        newUploads.push({
          file,
          preview: URL.createObjectURL(file),
          error: `File size exceeds ${item.documentType.maxFileSizeMb}MB limit`
        });
        return;
      }

      // Validate file type
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !item.documentType.allowedFileTypes.includes(fileExtension)) {
        newUploads.push({
          file,
          preview: URL.createObjectURL(file),
          error: `File type not allowed. Allowed types: ${item.documentType.allowedFileTypes.join(', ')}`
        });
        return;
      }

      newUploads.push({
        file,
        preview: URL.createObjectURL(file)
      });
    });

    setUploads(prev => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), ...newUploads]
    }));
  };

  const removeFile = (itemId: number, index: number) => {
    setUploads(prev => {
      const newUploads = { ...prev };
      if (newUploads[itemId]) {
        newUploads[itemId] = newUploads[itemId].filter((_, i) => i !== index);
      }
      return newUploads;
    });
  };

  const validateRequiredDocuments = (): boolean => {
    if (!request) return false;
    
    const missingTypes: string[] = [];
    
    request.items.forEach(item => {
      // Skip already submitted items in validation
      if (item.status === 'submitted') return;
      
      if (item.documentType.isRequired) {
        const itemUploads = uploads[item.id]?.filter(u => !u.error) || [];
        if (itemUploads.length === 0) {
          missingTypes.push(item.documentType.name);
        }
      }
    });
    
    if (missingTypes.length > 0) {
      setMissingRequiredTypes(missingTypes);
      setShowValidationModal(true);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!request) return;

    // Check if at least one new file is being uploaded
    const hasNewUploads = Object.keys(uploads).some(itemId => {
      const itemUploads = uploads[parseInt(itemId)]?.filter(u => !u.error) || [];
      return itemUploads.length > 0;
    });

    if (!hasNewUploads) {
      alert('Please upload at least one document before submitting.');
      return;
    }

    // Validate required documents (only check pending items)
    if (!validateRequiredDocuments()) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Prepare form data for file upload
      const formData = new FormData();
      
      // Add items data with file counts
      const items = request.items.map(item => {
        const itemUploads = uploads[item.id]?.filter(u => !u.error) || [];
        return {
          requestItemId: item.id,
          note: notes[item.id] || '',
          fileCount: itemUploads.length
        };
      });
      formData.append('items', JSON.stringify(items));

      // Add files in order by document type
      const filesByItem: { [itemId: number]: File[] } = {};
      request.items.forEach(item => {
        const itemUploads = uploads[item.id]?.filter(u => !u.error) || [];
        filesByItem[item.id] = itemUploads.map(upload => upload.file);
      });

      // Add files in the order they should be processed
      request.items.forEach(item => {
        const itemFiles = filesByItem[item.id] || [];
        itemFiles.forEach(file => {
          formData.append('files', file);
        });
      });

      // Submit to API
      const response = await import('../services/api').then(module => 
        module.publicApi.post(`/documents/public/submit/${token}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
      );

      if (response.data.success) {
        setSuccess(true);
        // Refetch request to show updated status
        await fetchRequest();
      } else {
        throw new Error(response.data.message || 'Submission failed');
      }
    } catch (err: any) {
      console.error('Error submitting documents:', err);
      setError(err.response?.data?.message || 'Failed to submit documents. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading document request...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <XCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Request Not Found</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (success && request) {
    const totalItems = request.items.length;
    const submittedItems = request.items.filter(item => item.status === 'submitted').length;
    const isPartialSubmission = submittedItems < totalItems;
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {isPartialSubmission ? 'Documents Submitted Partially' : 'All Documents Submitted Successfully'}
          </h2>
          {isPartialSubmission ? (
            <>
              <div className="mb-4">
                <div className="flex justify-center items-center space-x-2 mb-2">
                  <span className="text-2xl font-bold text-blue-600">{submittedItems}</span>
                  <span className="text-gray-500">/</span>
                  <span className="text-2xl font-bold text-gray-400">{totalItems}</span>
                </div>
                <p className="text-sm text-gray-600">documents submitted</p>
              </div>
              <p className="text-gray-600 mb-4">
                Thank you for submitting your documents. You can return to this link anytime to upload the remaining documents.
              </p>
              <button
                onClick={() => {
                  setSuccess(false);
                  setUploads({});
                  setNotes({});
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Upload Remaining Documents
              </button>
            </>
          ) : (
            <p className="text-gray-600">
              Thank you for submitting all your documents. We will review them and get back to you soon.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Show completion message only if ALL items are submitted
  if (request && request.status === 'submitted') {
    const allItemsSubmitted = request.items.every(item => item.status === 'submitted');
    
    if (allItemsSubmitted) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
            <CheckCircleIcon className="w-12 h-12 text-blue-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">All Documents Submitted</h2>
            <p className="text-gray-600 mb-2">
              You have already submitted all required documents for this request. Thank you!
            </p>
            <p className="text-sm text-gray-500">
              Status: <span className="font-medium text-gray-700">Under Review</span>
            </p>
          </div>
        </div>
      );
    }
  }

  if (!request) return null;

  // Calculate progress
  const totalItems = request.items.length;
  const submittedItems = request.items.filter(item => item.status === 'submitted').length;
  const pendingItems = totalItems - submittedItems;
  const progressPercentage = totalItems > 0 ? (submittedItems / totalItems) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-gray-900">{request.organization.name}</h1>
                {request.isReRequest && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                    Re-Submission Request
                  </span>
                )}
              </div>
              <p className="text-gray-600">Document Submission Request</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <UserIcon className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">
                  Hello {request.lead.firstName} {request.lead.lastName}
                </p>
                <p className="text-sm text-blue-700">
                  {pendingItems > 0 
                    ? `Please submit the ${pendingItems === totalItems ? 'requested' : 'remaining'} documents below. ${request.items.some(i => i.documentType.isRequired && i.status !== 'submitted') ? 'All required documents must be submitted.' : ''}`
                    : 'All documents have been submitted. Thank you!'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {totalItems > 0 && (
            <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Submission Progress</span>
                <span className="text-sm font-semibold text-blue-600">
                  {submittedItems} / {totalItems} Documents
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2"
                  style={{ width: `${progressPercentage}%` }}
                >
                  {progressPercentage > 15 && (
                    <span className="text-[10px] font-bold text-white">
                      {Math.round(progressPercentage)}%
                    </span>
                  )}
                </div>
              </div>
              {submittedItems > 0 && pendingItems > 0 && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  💡 You can return to this link anytime to upload the remaining documents
                </p>
              )}
            </div>
          )}
        </div>

        {/* Submission Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {request.items.map((item) => {
            const isAlreadySubmitted = item.status === 'submitted';
            
            return (
            <div key={item.id} className={`bg-white rounded-lg shadow-sm border p-6 ${isAlreadySubmitted ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <DocumentTextIcon className={`w-5 h-5 mr-2 ${isAlreadySubmitted ? 'text-green-600' : 'text-gray-400'}`} />
                      {item.documentType.name}
                      {item.documentType.isRequired && item.status !== 'submitted' && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      )}
                    </h3>
                    {isAlreadySubmitted && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                        Submitted ✓
                      </span>
                    )}
                  </div>
                  {item.documentType.description && (
                    <p className="text-sm text-gray-600 mt-1">{item.documentType.description}</p>
                  )}
                  
                  {isAlreadySubmitted && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-800">
                            This document has been successfully submitted.
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            No further action required for this document type.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Display rejection reasons/notes */}
                  {item.notes && item.notes.length > 0 && !isAlreadySubmitted && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="flex items-start">
                        <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-yellow-800 mb-2">Important Note:</h4>
                          {item.notes.map((note, index) => (
                            <p key={index} className="text-sm text-yellow-700 mb-1">
                              {note.note}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Only show upload section for pending items */}
              {!isAlreadySubmitted && (
              <div className="space-y-4">
                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Files
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                    <CloudArrowUpIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 mb-2">
                      Click to upload or drag and drop files here
                    </p>
                    <p className="text-xs text-gray-500">
                      Max size: {item.documentType.maxFileSizeMb}MB | 
                      Allowed types: {item.documentType.allowedFileTypes.join(', ')}
                    </p>
                    <input
                      type="file"
                      multiple
                      accept={item.documentType.allowedFileTypes.map(type => `.${type}`).join(',')}
                      onChange={(e) => handleFileSelect(item.id, e.target.files)}
                      className="hidden"
                      id={`file-upload-${item.id}`}
                    />
                    <label
                      htmlFor={`file-upload-${item.id}`}
                      className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer"
                    >
                      Choose Files
                    </label>
                  </div>
                </div>

                {/* Uploaded Files */}
                {uploads[item.id] && uploads[item.id].length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">Uploaded Files:</h4>
                    {uploads[item.id].map((upload, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{upload.file.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(upload.file.size)}</p>
                            {upload.error && (
                              <p className="text-xs text-red-600">{upload.error}</p>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(item.id, index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <XCircleIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Note */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note (Optional)
                  </label>
                  <textarea
                    value={notes[item.id] || ''}
                    onChange={(e) => setNotes(prev => ({ ...prev, [item.id]: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add any additional notes about this document..."
                  />
                </div>
              </div>
              )}
            </div>
            );
          })}

          {/* Submit Button */}
          {pendingItems > 0 && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting Documents...
                </div>
              ) : (
                submittedItems > 0 ? 'Submit Remaining Documents' : 'Submit Documents'
              )}
            </button>
          </div>
          )}
        </form>
      </div>

      {/* Validation Modal */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Required Documents Missing
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4">
                  {submittedItems > 0 
                    ? 'The following required documents still need to be uploaded:'
                    : 'Please upload the following required documents before submitting:'
                  }
                </p>
                <ul className="text-sm text-gray-700 text-left space-y-2">
                  {missingRequiredTypes.map((type, index) => (
                    <li key={index} className="flex items-center">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                      {type}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={() => setShowValidationModal(false)}
                  className="px-4 py-2 bg-blue-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  OK, I'll upload them
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentSubmissionForm;
